/**
 * Short internal error codes shown to people instead of raw error text (D-24). The full table, with causes and
 * fixes, is in docs/error-codes.md. The raw error is logged to the console in development builds only.
 *
 * Adding a code: map it here, give it a row in docs/error-codes.md and a case in authErrors.test.ts.
 * Prefixes: A = Firebase auth, G = native Google sign-in, N = network, C = cancelled (shown as nothing),
 * X = unknown. Never reuse or renumber a published code.
 */

/** Firebase JS SDK codes. */
const FIREBASE: Record<string, string> = {
  'auth/invalid-credential': 'A01',
  'auth/wrong-password': 'A01',
  'auth/user-not-found': 'A01',
  'auth/email-already-in-use': 'A02',
  'auth/weak-password': 'A03',
  'auth/invalid-email': 'A04',
  'auth/too-many-requests': 'A05',
  'auth/operation-not-allowed': 'A06',
  'auth/account-exists-with-different-credential': 'A07',
  'auth/user-disabled': 'A08',
  'auth/internal-error': 'A10',
  'auth/network-request-failed': 'N01',
  'lineage/no-id-token': 'G15',
};

/** Google Play services status codes, which the native Google sign-in puts at the start of its message ("10: …"). */
const GOOGLE_STATUS: Record<string, string> = { '10': 'G10', '12500': 'G11', '12501': 'G12', '16': 'G14', '7': 'N01' };
/** Credential Manager / One Tap codes, which appear in brackets ("[28444] …"). */
const GOOGLE_BRACKET: Record<string, string> = { '28444': 'G10', '28433': 'G14' };

/** Errors that mean the person backed out; callers show nothing for these. */
export const CANCELLED = 'C00';

const parts = (e: unknown) => {
  const { code, message } = (e ?? {}) as { code?: unknown; message?: unknown };
  return {
    code: typeof code === 'string' ? code : typeof code === 'number' ? String(code) : '',
    message: typeof message === 'string' ? message : typeof e === 'string' ? e : '',
  };
};

export const errorCode = (e: unknown): string => {
  const { code, message } = parts(e);
  if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') return CANCELLED;
  if (code in FIREBASE) return FIREBASE[code]!;
  if (/^auth\/requests-from-referer-.*-are-blocked/.test(code)) return 'A09';
  const status = /^\s*(\d+)\s*:/.exec(message)?.[1];
  if (status === '12501' || /cancel/i.test(message)) return CANCELLED;
  const bracket = /\[(\d{5})\]/.exec(message)?.[1];
  if (bracket && bracket in GOOGLE_BRACKET) return GOOGLE_BRACKET[bracket]!;
  if (status && status in GOOGLE_STATUS) return GOOGLE_STATUS[status]!;
  if (/DEVELOPER_ERROR|not set up correctly/i.test(message)) return 'G10';
  if (/no credentials available/i.test(message)) return 'G13';
  if (code.startsWith('auth/')) return 'A99';
  if (e instanceof TypeError || /failed to fetch|load failed|networkerror|network error/i.test(message)) return 'N02';
  return 'X00';
};

/** `text` followed by the internal code, e.g. "Something went wrong. (Error G10)". Logs the raw error in dev. */
export const withCode = (text: string, e: unknown): string => {
  if (import.meta.env.DEV) console.error(e);
  return `${text} (Error ${errorCode(e)})`;
};
